import { useContext, useEffect, useState } from "react";
import Button from "carbon-react/esm/components/button/__next__";
import DateInput from "carbon-react/esm/components/date";
import Loader from "carbon-react/esm/components/loader/__next__";
import Message from "carbon-react/esm/components/message";
import Textbox from "carbon-react/esm/components/textbox";
import { calculateExpectedPayment } from "@loan-management/calculations";

import { ApplicationApiError } from "../api/applicationApi";
import { getLoanView, performLoanAction, type LoanDetailResponse, type LoanOptionsResponse } from "../api/loanOperationsApi";
import { EmbeddedSessionContext } from "../auth/EmbeddedSessionProvider";
import { Option, SearchableSelect as Select } from "../components/SearchableSelect";

export const initialLoanDraft = {
  accrualFrequency: "monthly",
  assetId: "",
  assetIdentifier: "",
  assetType: "other",
  amortizationTermMonths: "",
  annualRate: "",
  balloonAmount: "0",
  borrowingEntityId: "",
  businessDayConvention: "none",
  calculationMethod: "simple_periodic",
  compoundingMethod: "none",
  collateralDescription: "",
  contractTermMonths: "",
  dayCountConvention: "actual_365",
  firstAccrualDate: "",
  firstExpectedBankPaymentDate: "",
  firstPaymentApplicationDate: "",
  fundingDate: "",
  interestOnlyEndDate: "",
  lenderId: "",
  loanCategory: "other",
  loanNumber: "",
  loanType: "amortizing",
  maturityDate: "",
  make: "",
  model: "",
  modelYear: "",
  name: "",
  originationDate: "",
  originalPrincipal: "",
  estimatedValue: "",
  paymentAmount: "",
  paymentFrequency: "monthly",
  paymentTiming: "arrears",
  sageFixedAssetId: "",
  valuationDate: "",
  vin: "",
};

type LoanDraft = typeof initialLoanDraft;
type FieldName = keyof LoanDraft;
type FieldErrors = Partial<Record<FieldName | `terms.${string}` | `assets.${string}`, string>>;

const categories = [
  ["personal", "Personal"],
  ["mortgage_real_estate", "Mortgage / real estate"],
  ["vehicle", "Vehicle"],
  ["equipment", "Equipment"],
  ["commercial_business", "Commercial / business"],
  ["construction", "Construction"],
  ["intercompany", "Intercompany"],
  ["student", "Student"],
  ["other", "Other"],
] as const;

export function NewLoanPage({ loanId, onNavigate }: { loanId?: string; onNavigate(path: string): void }) {
  const { api } = useContext(EmbeddedSessionContext);
  const [options, setOptions] = useState<LoanOptionsResponse | null>(null);
  const [draft, setDraft] = useState(initialLoanDraft);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [pageError, setPageError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const editing = loanId !== undefined;
  const expectedPayment = calculateDraftExpectedPayment(draft);

  useEffect(() => {
    if (!api) return;
    setLoading(true);
    void Promise.all([
      getLoanView<LoanOptionsResponse>(api, "options"),
      loanId ? getLoanView<LoanDetailResponse>(api, "loan", { loanId }) : Promise.resolve(null),
    ]).then(([nextOptions, detail]) => {
      setOptions(nextOptions);
      if (detail) setDraft(loanDraftFromDetail(detail));
    }).catch(() => setPageError("Loan setup could not be loaded."))
      .finally(() => setLoading(false));
  }, [api, loanId]);

  if (loading) return <Loader loaderLabel="Loading loan setup" size="medium" />;
  if (!options) return <Message open title="Loan setup unavailable" variant="error">{pageError}</Message>;

  const update = (name: FieldName, value: string) => {
    setDraft((current) => updateLoanDraft(current, name, value));
    setFieldErrors((current) => omitFieldError(current, name));
  };

  const save = async () => {
    if (!api) return;
    const validation = validateLoanDraft(draft);
    if (Object.keys(validation).length > 0) {
      setFieldErrors(validation);
      setPageError(`Correct the highlighted fields before ${editing ? "saving" : "creating"} the loan.`);
      focusFirstInvalid(validation);
      return;
    }

    setSaving(true);
    setPageError(null);
    setFieldErrors({});
    try {
      const response = await performLoanAction<{ loanId: string }>(api, {
        ...createLoanRequest(draft),
        action: editing ? "update_loan" : "create_loan",
        ...(loanId ? { loanId } : {}),
      });
      onNavigate(`/loans/${response.loanId}/terms`);
    } catch (error) {
      if (error instanceof ApplicationApiError) {
        const errors = error.fieldErrors as FieldErrors;
        setFieldErrors(errors);
        setPageError(Object.keys(errors).length > 0 ? `Correct the highlighted fields before ${editing ? "saving" : "creating"} the loan.` : error.message);
        focusFirstInvalid(errors);
      } else {
        setPageError(`Loan Management could not ${editing ? "save" : "create"} the draft loan.`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="operational-stack">
      {pageError && <Message open title={`Loan could not be ${editing ? "saved" : "created"}`} variant="error">{pageError}</Message>}

      <EditorSection title="Essentials">
        <p className="muted-text">Create the draft with the core facts. Dates, detailed terms, collateral, and accounting can be completed later.</p>
        <div className="form-grid">
          <TextField error={fieldError(fieldErrors, "loanNumber")} label="Loan ID" name="loanNumber" update={update} value={draft.loanNumber} />
          <TextField error={fieldError(fieldErrors, "name")} label="Loan name" name="name" update={update} value={draft.name} />
          <Select error={fieldError(fieldErrors, "borrowingEntityId")} id="borrowingEntityId" label="Borrowing entity" onChange={(event) => update("borrowingEntityId", event.target.value)} required value={draft.borrowingEntityId}>
            <Option text="Select an entity" value="" />
            {options.entities.map((item) => <Option key={item.id} text={`${item.display_name} (${item.sage_record_no})`} value={item.id} />)}
          </Select>
          <Select error={fieldError(fieldErrors, "lenderId")} id="lenderId" label="Lender" onChange={(event) => update("lenderId", event.target.value)} required value={draft.lenderId}>
            <Option text="Select a lender" value="" />
            {options.lenders.map((item) => <Option key={item.id} text={`${item.name} (${item.code})`} value={item.id} />)}
          </Select>
          <Select error={fieldError(fieldErrors, "loanCategory")} id="loanCategory" label="Loan category" onChange={(event) => update("loanCategory", event.target.value)} required value={draft.loanCategory}>
            {categories.map(([value, label]) => <Option key={value} text={label} value={value} />)}
          </Select>
          <Select error={fieldError(fieldErrors, "loanType")} id="loanType" label="Repayment structure" onChange={(event) => update("loanType", event.target.value)} required value={draft.loanType}>
            <Option text="Amortizing" value="amortizing" />
            <Option text="Interest only" value="interest_only" />
            <Option text="Interest only, then amortizing" value="interest_only_then_amortizing" />
          </Select>
          <TextField decimal error={fieldError(fieldErrors, "originalPrincipal")} label="Original principal" name="originalPrincipal" update={update} value={draft.originalPrincipal} />
        </div>
      </EditorSection>

      <EditorSection title="Repayment and interest">
        <div className="form-grid">
          <Select id="paymentFrequency" label="Payment frequency" onChange={(event) => update("paymentFrequency", event.target.value)} value={draft.paymentFrequency}>
            <Option text="Monthly" value="monthly" /><Option text="Quarterly" value="quarterly" /><Option text="Annual" value="annual" />
          </Select>
          <Select id="accrualFrequency" label="Interest accrual frequency" onChange={(event) => update("accrualFrequency", event.target.value)} value={draft.accrualFrequency}>
            <Option text="Daily" value="daily" /><Option text="Monthly" value="monthly" /><Option text="Quarterly" value="quarterly" /><Option text="Annual" value="annual" />
          </Select>
          <Select id="calculationMethod" label="Interest calculation" onChange={(event) => update("calculationMethod", event.target.value)} value={draft.calculationMethod}>
            <Option text="Simple periodic" value="simple_periodic" /><Option text="Daily simple" value="daily_simple" />
          </Select>
          <Select id="compoundingMethod" label="Compounding" onChange={(event) => update("compoundingMethod", event.target.value)} value={draft.compoundingMethod}>
            <Option text="None" value="none" /><Option text="Monthly" value="monthly" />
          </Select>
          <Select id="paymentTiming" label="Payment timing" onChange={(event) => update("paymentTiming", event.target.value)} value={draft.paymentTiming}>
            <Option text="In arrears" value="arrears" /><Option text="In advance" value="advance" />
          </Select>
          <Select id="dayCountConvention" label="Day-count convention" onChange={(event) => update("dayCountConvention", event.target.value)} value={draft.dayCountConvention}>
            <Option text="Actual/365" value="actual_365" /><Option text="Actual/360" value="actual_360" /><Option text="30/360" value="30_360" /><Option text="Actual/Actual" value="actual_actual" />
          </Select>
          <Select id="businessDayConvention" label="Business-day convention" onChange={(event) => update("businessDayConvention", event.target.value)} value={draft.businessDayConvention}>
            <Option text="None" value="none" /><Option text="Following" value="following" /><Option text="Modified following" value="modified_following" /><Option text="Preceding" value="preceding" />
          </Select>
          <TextField decimal error={fieldError(fieldErrors, "annualRate")} label="Annual rate (%)" name="annualRate" optional update={update} value={draft.annualRate} />
          <TextField decimal label="Expected payment" name="paymentAmount" optional readOnly update={update} value={expectedPayment} />
          <TextField error={fieldError(fieldErrors, "contractTermMonths")} label="Contract term (months)" name="contractTermMonths" numeric optional update={update} value={draft.contractTermMonths} />
          <TextField error={fieldError(fieldErrors, "amortizationTermMonths")} label="Amortization term (months)" name="amortizationTermMonths" numeric optional update={update} value={draft.amortizationTermMonths} />
          <TextField decimal label="Balloon amount" name="balloonAmount" optional update={update} value={draft.balloonAmount} />
        </div>
      </EditorSection>

      <EditorSection title="Dates">
        <div className="form-grid">
          <DateField errors={fieldErrors} label="Loan start (origination) date" name="originationDate" update={update} value={draft.originationDate} />
          <DateField errors={fieldErrors} label="Funding date" minDate={draft.originationDate || undefined} name="fundingDate" update={update} value={draft.fundingDate} />
          <DateField errors={fieldErrors} label="First accrual date" minDate={draft.fundingDate || undefined} name="firstAccrualDate" update={update} value={draft.firstAccrualDate} />
          <DateField errors={fieldErrors} label="First payment application date" minDate={draft.firstAccrualDate || undefined} name="firstPaymentApplicationDate" update={update} value={draft.firstPaymentApplicationDate} />
          <DateField errors={fieldErrors} label="First expected bank payment" minDate={draft.firstPaymentApplicationDate || undefined} name="firstExpectedBankPaymentDate" update={update} value={draft.firstExpectedBankPaymentDate} />
          <DateField errors={fieldErrors} label="Maturity date" minDate={draft.firstAccrualDate || undefined} name="maturityDate" readOnly update={update} value={draft.maturityDate} />
          {draft.loanType === "interest_only_then_amortizing" && <DateField errors={fieldErrors} label="Interest-only end date" minDate={draft.firstAccrualDate || undefined} name="interestOnlyEndDate" update={update} value={draft.interestOnlyEndDate} />}
        </div>
      </EditorSection>

      <EditorSection title="Optional collateral">
        <p className="muted-text">Add collateral when the agreement is secured. Leave the description empty for an unsecured loan.</p>
        <div className="form-grid">
          <Select id="assetType" label="Collateral type" onChange={(event) => update("assetType", event.target.value)} value={draft.assetType}>
            <Option text="Real estate" value="real_estate" /><Option text="Vehicle" value="vehicle" /><Option text="Equipment" value="equipment" />
            <Option text="Inventory" value="inventory" /><Option text="Receivable" value="receivable" /><Option text="Securities" value="securities" />
            <Option text="Guarantee" value="guarantee" /><Option text="Other" value="other" />
          </Select>
          <TextField error={fieldErrors["assets.description"]} label="Collateral description" name="collateralDescription" optional update={update} value={draft.collateralDescription} />
          <TextField label="Asset identifier" name="assetIdentifier" optional update={update} value={draft.assetIdentifier} />
          <TextField decimal error={fieldErrors["assets.estimatedValue"]} label="Estimated value" name="estimatedValue" optional update={update} value={draft.estimatedValue} />
          <DateField errors={fieldErrors} label="Valuation date" name="valuationDate" update={update} value={draft.valuationDate} />
          <TextField label="Sage fixed asset ID" name="sageFixedAssetId" optional update={update} value={draft.sageFixedAssetId} />
          {draft.assetType === "vehicle" && <>
            <TextField error={fieldErrors["assets.vin"]} label="VIN" name="vin" optional update={update} value={draft.vin} />
            <TextField error={fieldErrors["assets.modelYear"]} label="Model year" name="modelYear" numeric optional update={update} value={draft.modelYear} />
            <TextField label="Make" name="make" optional update={update} value={draft.make} />
            <TextField label="Model" name="model" optional update={update} value={draft.model} />
          </>}
        </div>
      </EditorSection>

      <EditorSection title="Loan accounting">
        <p className="muted-text">Loan-specific accounts and dimensions can be configured after the draft is created and are required before schedule activation.</p>
      </EditorSection>

      <div className="button-row editor-actions">
        <Button onClick={() => onNavigate(loanId ? `/loans/${loanId}/terms` : "/loans")} variant="default" variantType="tertiary">Cancel</Button>
        <Button disabled={saving || options.entities.length === 0 || options.lenders.length === 0} onClick={() => void save()} variant="default" variantType="primary">
          {saving ? "Saving" : editing ? "Save draft version" : "Create draft loan"}
        </Button>
      </div>
    </div>
  );
}

function EditorSection({ children, title }: { children: React.ReactNode; title: string }) {
  const id = `${title.toLowerCase().replaceAll(/[^a-z]+/gu, "-")}-heading`;
  return <section aria-labelledby={id} className="wizard-section"><h2 id={id}>{title}</h2>{children}</section>;
}

function TextField({ decimal = false, error, label, name, numeric = false, optional = false, readOnly = false, update, value }: {
  decimal?: boolean;
  error?: string;
  label: string;
  name: FieldName;
  numeric?: boolean;
  optional?: boolean;
  readOnly?: boolean;
  update(name: FieldName, value: string): void;
  value: string;
}) {
  return <Textbox error={error} id={name} inputMode={decimal ? "decimal" : numeric ? "numeric" : "text"} label={`${label}${optional ? " (calculated when terms are complete)" : ""}`} onChange={(event) => update(name, event.target.value)} readOnly={readOnly} required={!optional} value={value} />;
}

function DateField({ errors, label, minDate, name, readOnly = false, update, value }: {
  errors: FieldErrors;
  label: string;
  minDate?: string;
  name: FieldName;
  readOnly?: boolean;
  update(name: FieldName, value: string): void;
  value: string;
}) {
  return <DateInput
    allowEmptyValue
    dateFormatOverride="M/d/yyyy"
    datePickerAriaLabel={`Choose ${label.toLowerCase()}`}
    error={fieldError(errors, name)}
    id={name}
    inputHint={readOnly ? "Calculated from loan start and contract term" : "M/D/YYYY"}
    inputWidth={100}
    label={`${label} (optional)`}
    minDate={minDate}
    maxWidth="100%"
    onChange={(event) => update(name, normalizeCalendarDate(event.target.value.rawValue ?? event.target.value.formattedValue))}
    pickerProps={{ captionLayout: "dropdown", endMonth: new Date(2200, 11), startMonth: new Date(1900, 0) }}
    readOnly={readOnly}
    value={formatDateInputValue(value)}
  />;
}

export function validateLoanDraft(draft: LoanDraft): FieldErrors {
  const errors: FieldErrors = {};
  const required: Array<[FieldName, string]> = [
    ["loanNumber", "Enter a Loan ID"],
    ["name", "Enter a loan name"],
    ["borrowingEntityId", "Select a borrowing entity"],
    ["lenderId", "Select a lender"],
    ["loanCategory", "Select a loan category"],
    ["loanType", "Select a repayment structure"],
    ["originalPrincipal", "Enter an original principal greater than zero"],
  ];
  for (const [name, message] of required) if (!draft[name].trim()) errors[name] = message;
  if (draft.originalPrincipal && (!isDecimal(draft.originalPrincipal) || Number(draft.originalPrincipal) <= 0)) errors.originalPrincipal = "Original principal must be greater than zero";
  if (draft.annualRate && !isDecimal(draft.annualRate)) errors.annualRate = "Annual rate must be zero or greater";
  const hasCollateral=Boolean(draft.assetIdentifier||draft.estimatedValue||draft.valuationDate||draft.vin||draft.modelYear||draft.make||draft.model||draft.sageFixedAssetId);
  if(hasCollateral&&!draft.collateralDescription.trim())errors["assets.description"]="Enter a collateral description";
  if(draft.estimatedValue&&!isDecimal(draft.estimatedValue))errors["assets.estimatedValue"]="Estimated value must be zero or greater";
  if(draft.assetType==="vehicle"&&draft.vin&&!/^[A-HJ-NPR-Z0-9]{17}$/u.test(draft.vin.replace(/[\s-]/gu,"").toUpperCase()))errors["assets.vin"]="VIN must contain 17 valid characters";
  for (const name of ["amortizationTermMonths", "contractTermMonths"] as const) {
    if (draft[name] && (!/^\d+$/u.test(draft[name]) || Number(draft[name]) <= 0)) errors[name] = "Enter a whole number greater than zero";
  }
  const orderedDates: Array<[FieldName, FieldName, string]> = [
    ["originationDate", "fundingDate", "Funding date cannot be before origination date"],
    ["fundingDate", "firstAccrualDate", "First accrual date cannot be before funding date"],
    ["firstAccrualDate", "firstPaymentApplicationDate", "First payment application date cannot be before first accrual date"],
    ["firstPaymentApplicationDate", "firstExpectedBankPaymentDate", "First expected bank payment cannot be before first payment application date"],
    ["firstAccrualDate", "maturityDate", "Maturity date must be after the first accrual date"],
  ];
  for (const [earlier, later, message] of orderedDates) if (draft[earlier] && draft[later] && draft[later] < draft[earlier]) errors[later] = message;
  if (draft.annualRate && !draft.firstAccrualDate) errors.firstAccrualDate = "Choose a first accrual date for this rate";
  if (draft.firstPaymentApplicationDate && draft.maturityDate && draft.firstPaymentApplicationDate > draft.maturityDate) errors.firstPaymentApplicationDate = "First payment application date cannot be after maturity";
  const scheduledPayments=calculateScheduledPaymentCount(draft.firstPaymentApplicationDate,draft.maturityDate,draft.paymentFrequency);
  const expectedPayments=/^\d+$/u.test(draft.amortizationTermMonths)
    ? Math.ceil(Number(draft.amortizationTermMonths)/(draft.paymentFrequency==="monthly"?1:draft.paymentFrequency==="quarterly"?3:12))
    : null;
  if(scheduledPayments!==null&&expectedPayments!==null&&scheduledPayments!==expectedPayments){
    errors.firstPaymentApplicationDate=`First payment and maturity dates produce ${scheduledPayments} payments; the amortization term requires ${expectedPayments}`;
  }
  if (draft.paymentTiming === "advance" && draft.firstAccrualDate && draft.firstPaymentApplicationDate && draft.firstAccrualDate !== draft.firstPaymentApplicationDate) errors.firstPaymentApplicationDate = "For payments in advance, first payment application must equal first accrual date";
  if (draft.interestOnlyEndDate && draft.firstAccrualDate && draft.interestOnlyEndDate < draft.firstAccrualDate) errors.interestOnlyEndDate = "Interest-only end date cannot be before first accrual";
  if (draft.loanType === "interest_only_then_amortizing" && draft.interestOnlyEndDate && draft.maturityDate && draft.interestOnlyEndDate >= draft.maturityDate) errors.interestOnlyEndDate = "Interest-only end date must be before maturity";
  return errors;
}

function createLoanRequest(draft: LoanDraft): Record<string, unknown> {
  const expectedPayment=calculateDraftExpectedPayment(draft)||null;
  return {
    action: "create_loan",
    assets: draft.collateralDescription ? [{
      assetIdentifier:draft.assetIdentifier||null,assetType:draft.assetType,description:draft.collateralDescription,
      estimatedValue:draft.estimatedValue||null,id:draft.assetId||null,make:draft.make||null,model:draft.model||null,
      modelYear:draft.modelYear?Number(draft.modelYear):null,sageFixedAssetId:draft.sageFixedAssetId||null,
      valuationDate:draft.valuationDate||null,vin:draft.vin||null,
    }] : [],
    borrowingEntityId: draft.borrowingEntityId,
    fees: [],
    lenderId: draft.lenderId,
    loanCategory: draft.loanCategory,
    loanNumber: draft.loanNumber,
    loanType: draft.loanType,
    name: draft.name,
    originalPrincipal: draft.originalPrincipal,
    rateSegments: draft.annualRate && draft.firstAccrualDate ? [{ annualRate: percentToDecimal(draft.annualRate), effectiveDate: draft.firstAccrualDate, paymentAmount: expectedPayment }] : [],
    terms: {
      accrualFrequency: draft.accrualFrequency,
      amortizationTermMonths: optionalInteger(draft.amortizationTermMonths),
      balloonAmount: draft.balloonAmount || null,
      balloonMethod: "fixed",
      businessDayConvention: draft.businessDayConvention,
      calculationMethod: draft.calculationMethod,
      compoundingMethod: draft.compoundingMethod,
      contractTermMonths: optionalInteger(draft.contractTermMonths),
      dayCountConvention: draft.dayCountConvention,
      firstAccrualDate: draft.firstAccrualDate || null,
      firstExpectedBankPaymentDate: draft.firstExpectedBankPaymentDate || null,
      firstPaymentApplicationDate: draft.firstPaymentApplicationDate || null,
      fundingDate: draft.fundingDate || null,
      interestOnlyEndDate: draft.interestOnlyEndDate || null,
      maturityDate: draft.maturityDate || null,
      originationDate: draft.originationDate || null,
      paymentAmount: expectedPayment,
      paymentFrequency: draft.paymentFrequency,
      paymentTiming: draft.paymentTiming,
    },
  };
}

export function loanDraftFromDetail(data: LoanDetailResponse): LoanDraft {
  const term = data.terms[0] ?? {};
  const termId = String(term.id ?? "");
  const rate = data.rates.find((item) => String(item.term_version_id) === termId);
  const asset=data.assets?.[0];
  const value = (source:Record<string,unknown>,key:string) => source[key] === null || source[key] === undefined ? "" : String(source[key]);
  const dateValue = (source:Record<string,unknown>,key:string) => normalizeCalendarDate(source[key]);
  return {
    ...initialLoanDraft,
    accrualFrequency:value(term,"accrual_frequency") || initialLoanDraft.accrualFrequency,
    assetId:asset?.id??"",
    assetIdentifier:asset?.asset_identifier??"",
    assetType:asset?.asset_type??initialLoanDraft.assetType,
    amortizationTermMonths:value(term,"amortization_term_months"),
    annualRate:rate ? String(Number(rate.annual_rate) * 100) : "",
    balloonAmount:value(term,"balloon_amount") || initialLoanDraft.balloonAmount,
    borrowingEntityId:value(data.loan,"borrowing_entity_id"),
    businessDayConvention:value(term,"business_day_convention") || initialLoanDraft.businessDayConvention,
    calculationMethod:value(term,"calculation_method") || initialLoanDraft.calculationMethod,
    compoundingMethod:value(term,"compounding_method") || initialLoanDraft.compoundingMethod,
    collateralDescription:asset?.description??"",
    contractTermMonths:value(term,"contract_term_months"),
    dayCountConvention:value(term,"day_count_convention") || initialLoanDraft.dayCountConvention,
    firstAccrualDate:dateValue(term,"first_accrual_date"),
    firstExpectedBankPaymentDate:dateValue(term,"first_expected_bank_payment_date"),
    firstPaymentApplicationDate:dateValue(term,"first_payment_application_date"),
    fundingDate:dateValue(term,"funding_date"),
    interestOnlyEndDate:dateValue(term,"interest_only_end_date"),
    lenderId:String(data.lenders.find((lender) => lender.is_primary)?.id ?? ""),
    loanCategory:value(data.loan,"loan_category") || initialLoanDraft.loanCategory,
    loanNumber:value(data.loan,"loan_number"),
    loanType:value(data.loan,"loan_type") || initialLoanDraft.loanType,
    maturityDate:dateValue(term,"maturity_date"),
    make:asset?.make??"",
    model:asset?.model??"",
    modelYear:asset?.model_year===null||asset?.model_year===undefined?"":String(asset.model_year),
    name:value(data.loan,"name"),
    originationDate:dateValue(term,"origination_date"),
    originalPrincipal:value(term,"original_principal"),
    estimatedValue:asset?.estimated_value??"",
    paymentAmount:value(term,"payment_amount"),
    paymentFrequency:value(term,"payment_frequency") || initialLoanDraft.paymentFrequency,
    paymentTiming:value(term,"payment_timing") || initialLoanDraft.paymentTiming,
    sageFixedAssetId:asset?.sage_fixed_asset_id??"",
    valuationDate:normalizeCalendarDate(asset?.valuation_date??""),
    vin:asset?.vin??"",
  };
}

function omitFieldError(errors: FieldErrors, name: FieldName): FieldErrors {
  const next = { ...errors };
  delete next[name];
  delete next[`terms.${name}`];
  const collateralFields:Partial<Record<FieldName,string>>={collateralDescription:"description",estimatedValue:"estimatedValue",modelYear:"modelYear",vin:"vin"};
  const collateralField=collateralFields[name];
  if(collateralField)delete next[`assets.${collateralField}`];
  return next;
}

function fieldError(errors: FieldErrors, name: FieldName): string | undefined {
  return errors[name] ?? errors[`terms.${name}`];
}

export function updateLoanDraft(draft:LoanDraft,name:FieldName,value:string):LoanDraft {
  const next={...draft,[name]:value};
  if(name!=="originationDate"&&name!=="contractTermMonths")return next;
  const contractMonths=name==="contractTermMonths"?value:draft.contractTermMonths;
  const startDate=name==="originationDate"?value:draft.originationDate;
  if(name==="contractTermMonths")next.amortizationTermMonths=value;
  next.maturityDate=calculateMaturityDate(startDate,contractMonths);
  return next;
}

export function calculateMaturityDate(startDate:string,contractMonths:string):string {
  const normalized=normalizeCalendarDate(startDate);
  if(!/^\d{4}-\d{2}-\d{2}$/u.test(normalized)||!/^\d+$/u.test(contractMonths)||Number(contractMonths)<=0)return "";
  const [year,month,day]=normalized.split("-").map(Number) as [number,number,number];
  const targetMonth=month-1+Number(contractMonths);
  const targetYear=year+Math.floor(targetMonth/12);
  const normalizedMonth=((targetMonth%12)+12)%12;
  const lastDay=new Date(Date.UTC(targetYear,normalizedMonth+1,0)).getUTCDate();
  return `${String(targetYear).padStart(4,"0")}-${String(normalizedMonth+1).padStart(2,"0")}-${String(Math.min(day,lastDay)).padStart(2,"0")}`;
}

export function calculateScheduledPaymentCount(firstPaymentDate:string,maturityDate:string,paymentFrequency:string):number|null {
  if(!/^\d{4}-\d{2}-\d{2}$/u.test(firstPaymentDate)||!/^\d{4}-\d{2}-\d{2}$/u.test(maturityDate))return null;
  const first=new Date(`${firstPaymentDate}T00:00:00.000Z`);const maturity=new Date(`${maturityDate}T00:00:00.000Z`);
  if(Number.isNaN(first.getTime())||Number.isNaN(maturity.getTime())||first>maturity)return null;
  const frequencyMonths=paymentFrequency==="monthly"?1:paymentFrequency==="quarterly"?3:12;
  let count=0;let current=first;
  while(current<maturity){count+=1;current=addCalendarMonths(first,frequencyMonths*count);}
  return current.getTime()===maturity.getTime()?count+1:count+1;
}

function addCalendarMonths(date:Date,months:number):Date {
  const targetMonth=date.getUTCMonth()+months;const targetYear=date.getUTCFullYear()+Math.floor(targetMonth/12);
  const normalizedMonth=((targetMonth%12)+12)%12;const lastDay=new Date(Date.UTC(targetYear,normalizedMonth+1,0)).getUTCDate();
  return new Date(Date.UTC(targetYear,normalizedMonth,Math.min(date.getUTCDate(),lastDay)));
}

export function normalizeCalendarDate(value:unknown):string {
  if(value instanceof Date&&!Number.isNaN(value.getTime()))return value.toISOString().slice(0,10);
  if(typeof value!=="string")return "";
  const match=/^(\d{4}-\d{2}-\d{2})/u.exec(value.trim());
  if(match)return match[1]!;
  const displayMatch=/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/u.exec(value.trim());
  if(!displayMatch)return value.trim();
  return `${displayMatch[3]}-${displayMatch[1]!.padStart(2,"0")}-${displayMatch[2]!.padStart(2,"0")}`;
}

function formatDateInputValue(value:string):string {
  const match=/^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  return match?`${Number(match[2])}/${Number(match[3])}/${match[1]}`:value;
}

function focusFirstInvalid(errors: FieldErrors) {
  const first = Object.keys(errors)[0]?.replace(/^terms\./u, "");
  if (first) requestAnimationFrame(() => document.getElementById(first)?.focus());
}

function isDecimal(value: string) { return /^\d+(?:\.\d+)?$/u.test(value); }
function optionalInteger(value: string) { return value ? Number(value) : null; }
function percentToDecimal(value:string):string {
  const [integer,fraction=""]=value.split(".");
  return `${BigInt(integer||"0")/100n}.${`${String(BigInt(integer||"0")%100n).padStart(2,"0")}${fraction}`.replace(/0+$/u,"")||"0"}`;
}

export function calculateDraftExpectedPayment(draft:LoanDraft):string {
  if(!isDecimal(draft.originalPrincipal)||!isDecimal(draft.annualRate)||!/^\d+$/u.test(draft.amortizationTermMonths)||!isDecimal(draft.balloonAmount||"0"))return "";
  try{return calculateExpectedPayment({
    amortizationTermMonths:Number(draft.amortizationTermMonths),annualRate:percentToDecimal(draft.annualRate),
    balloonAmount:draft.balloonAmount||"0",originalPrincipal:draft.originalPrincipal,
    paymentFrequency:draft.paymentFrequency as "monthly"|"quarterly"|"annual",paymentTiming:draft.paymentTiming as "advance"|"arrears",
  });}catch{return "";}
}